import React from "react";
import { connect } from "react-redux";
import Select, { components } from "react-select";
import * as t from "../../../lib/backend/manufacturing2.generated.types";
import { translate } from "../../IntlProviderWrapper";
import { getBackend as getBackend2 } from "../../../lib/backend/Backend2";
import { AVAILABLE_TYPES, INSIGHT_FILTERS } from "../../../lib/Insights";
import { SET_INSIGHTS_ACTIONS_TYPES } from "./reducers/common";
import { INSIGHT_TYPES, LINE_GROUP_TAGS } from "../../../lib/ManufacturingTags.generated";
import * as BusinessLogic from "../../../lib/BusinessLogic";
import { removeMarkdownFromText } from "../../../lib/Util";

import type { Dispatch } from "redux";
import type { ReduxState } from "./reducers/reducers";
import type { SetInsightTypesActions } from "./reducers/common";
import type { AvailableInsightTypes } from "../../../lib/Insights";

import img_tool_setup from "../../../img/tool-setup-icon.png";
import img_downtime from "../../../img/gantt_downtime_01.svg";

type OwnProps = {
    hide_insight_types?: AvailableInsightTypes[];
};

type Props = OwnProps & ReduxProps;

type State = {
    insight_types_translated: t.IInsightType[];
};

const SelectStyles = {
    option: (provided: React.CSSProperties) => ({
        ...provided,
        backgroundColor: "unset",
        ":active": "unset",
    }),
    indicatorSeparator: () => ({
        display: "none",
    }),
    container: (style: React.CSSProperties) => ({ ...style, minWidth: 300 }),
};

const MultiValue = ({ index, getValue }) =>
    !index && translate("common.show_insights", "Show insights") + ` (${getValue().length})`;

const Option = props => {
    const style: React.CSSProperties = {
        justifyContent: "flex-end",
        width: "15px",
        height: "25px",
        border: "1px #e8e8e8 solid",
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundImage: "none",
        backgroundColor: "none",
    };

    const orange_insight_types = [
        INSIGHT_TYPES.operation_no_input_material,
        INSIGHT_TYPES.tool_overlap,
        INSIGHT_FILTERS.filter_account_for_deliveries,
        INSIGHT_FILTERS.filter_include_verp,
        INSIGHT_FILTERS.filter_use_safety_stock,
    ];

    if (props.value === INSIGHT_TYPES.man_downtime) {
        style.backgroundImage = `url(${img_downtime})`;
    } else if (props.value === INSIGHT_TYPES.tool_setup) {
        style.backgroundImage = `url(${img_tool_setup})`;
    } else if (orange_insight_types.includes(props.value)) {
        style.backgroundColor = "#FFB68F";
    } else if (props.value === INSIGHT_TYPES.operation_delay_downstream) {
        style.backgroundColor = "#F99F9B";
    } else if (props.value === INSIGHT_TYPES.operation_constraint_violated) {
        style.backgroundColor = "#F99F9B";
    } else if (props.value === INSIGHT_TYPES.freeze_order_move) {
        style.borderStyle = "dotted";
        style.borderColor = "#0000FF";
        style.borderWidth = "medium";
    } else if (props.value === INSIGHT_TYPES.conditional_capacity_violated) {
        style.backgroundColor = "#F99F9B";
    } else if (props.value === INSIGHT_TYPES.planned_date_diff) {
        style.backgroundColor = "#F99F9B";
    } else if (props.value === INSIGHT_TYPES.partially_scheduled) {
        style.borderStyle = "dotted";
        style.borderColor = "#616e7c";
        style.borderWidth = "medium";
    } else if (props.value === INSIGHT_TYPES.operation_exceeds_deadline) {
        style.borderStyle = "solid";
        style.borderColor = "#dc3545";
        style.borderWidth = "2px";
    }

    return (
        <div>
            <components.Option {...props}>
                <div className="d-flex">
                    <div className="d-flex align-items-center w-100">
                        <input type="checkbox" checked={props.isSelected} onChange={() => null} />{" "}
                        <label className="ml-1">{props.label}</label>
                    </div>
                    <div key={props.value} style={style}></div>
                </div>
            </components.Option>
        </div>
    );
};

type SelectOptionItem = {
    value: AvailableInsightTypes;
    label: string;
};

class InsightTypesSelect extends React.Component<Props, State> {
    state: State = {
        insight_types_translated: [],
    };

    async componentDidMount() {
        const res = await getBackend2().manufacturing.getInsightTypes({});
        this.setState({ insight_types_translated: res.insight_types });
    }

    onChange = (selected: SelectOptionItem[]) => {
        this.props.reduxDispatch({ type: SET_INSIGHTS_ACTIONS_TYPES, data: selected.map(s => s.value) });
    };

    mapOptions = () => {
        const show_conditional_capacity_constraint_insights = BusinessLogic.getLineGroupTagBool(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.extract_conditional_capacity_constraint_insights,
            false
        );
        const show_planned_date_diff_insights = BusinessLogic.getLineGroupTagBool(
            this.props.line_group_uuid,
            LINE_GROUP_TAGS.extract_planned_date_diff_insights,
            false
        );
        if (this.state.insight_types_translated.length > 0) {
            let available_types = AVAILABLE_TYPES;

            if (this.props.hide_insight_types) {
                available_types = AVAILABLE_TYPES.filter(type => !(this.props.hide_insight_types ?? []).includes(type));
            }

            if (!show_conditional_capacity_constraint_insights) {
                available_types = available_types.filter(type => type != INSIGHT_TYPES.conditional_capacity_violated);
            }

            if (!show_planned_date_diff_insights) {
                available_types = available_types.filter(type => type != INSIGHT_TYPES.planned_date_diff);
            }

            return available_types.map(type => {
                const insight_type = this.state.insight_types_translated.find(el => el.code === type);
                let label = insight_type?.title;
                if (label === undefined || label === "UNKNOWN") {
                    label = translate(`Manufacturing.InsightTypesSelect.${type}`, type);
                }
                label = removeMarkdownFromText(label, ["**"]);
                return {
                    label,
                    value: type,
                };
            });
        }
        return [];
    };

    getValues = () => {
        return this.mapOptions().filter(o => this.props.insight_types.includes(o.value));
    };

    render() {
        return (
            <div id="insight-types-select">
                <Select
                    closeMenuOnSelect={false}
                    isMulti
                    components={{ Option, MultiValue }}
                    hideSelectedOptions={false}
                    isClearable={false}
                    options={this.mapOptions()}
                    backspaceRemovesValue={false}
                    onChange={this.onChange}
                    styles={SelectStyles}
                    value={this.getValues()}
                    placeholder={translate("common.show_insights", "Show insights")}
                    theme={theme => ({
                        ...theme,
                        spacing: {
                            ...theme.spacing,
                            baseUnit: 2,
                            controlHeight: 30,
                        },
                    })}
                />
            </div>
        );
    }
}

const mapState = (state: ReduxState) => ({
    insight_types: state.standalone_gantt.insight_types,
    line_group_uuid: state.gantt_chart_properties.line_group_uuid,
});

const mapDispatch = (dispatch: Dispatch<SetInsightTypesActions>) => ({
    reduxDispatch: dispatch,
});

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, {}, ReduxState>(
    mapState,
    mapDispatch
)(InsightTypesSelect);
